import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Checkbox,
  Button,
  FormControlLabel,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  CircularProgress,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  useTheme,
  alpha,
  Stack,
  Tooltip,
  InputAdornment,
  Fade,
  Grid,
  Alert
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ClearIcon from '@mui/icons-material/Clear';
import SaveIcon from '@mui/icons-material/Save';
import ClassIcon from '@mui/icons-material/Class';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import ViewListIcon from '@mui/icons-material/ViewList';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import SearchIcon from '@mui/icons-material/Search';
import { httpRequest } from 'src/services/httpRequest';
import toast from 'react-hot-toast';
import { useSelector } from 'react-redux';
import { RootState } from 'src/redux-store/appStore';

interface BidType {
  TypeID: number;
  Type: string;
  Active: boolean;
  RowStatus: number;
  CreateDate: string;
  CreatedBy: number;
  UpdateDate: string | null;
  UpdatedBy: number | null;
  DeleteDate: string | null;
  DeletedBy: number | null;
  TownshipID: number;
}

interface ApiResponse {
  status: string;
  message: string;
  data: {
    totalRecords: number;
    currentPage: number;
    totalPages: number;
    data: BidType[];
  };
}

interface FormErrors {
  bidType: string;
}

interface TouchedFields {
  bidType: boolean;
}

const BidTypeMaster: React.FC = () => {
  const theme = useTheme();

  // State management
  const [bidTypes, setBidTypes] = useState<BidType[]>([]);
  const [newBidType, setNewBidType] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [touched, setTouched] = useState<TouchedFields>({
    bidType: false
  });
  const [errors, setErrors] = useState<FormErrors>({
    bidType: ''
  });
  const [errorMessage, setErrorMessage] = useState<string>('');

  // We don't need to track these separately since they're part of the BidType object
  // when editing, and we access them directly from there
  const [message, setMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);
  const userDetails = useSelector((state: RootState) => state.user.userDetails);

  const userRole = userDetails.UserRole || '';

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => {
      clearTimeout(timer);
    };
  }, [searchTerm]);

  const fetchBidTypes = async () => {
    try {
      setTableLoading(true);
      const response = (await httpRequest(
        'GET',
        `/bid_portal/type/get_all?Page=${
          page + 1
        }&Size=${rowsPerPage}&Active=false${
          debouncedSearchTerm ? `&Type=${debouncedSearchTerm}` : ''
        }`
      )) as ApiResponse;

      if (response.status === 'success' && response.data) {
        setBidTypes(response.data.data);
        setTotalRecords(response.data.totalRecords);
      }
    } catch (error) {
      toast.error('Failed to fetch bid types');
    } finally {
      setTableLoading(false);
    }
  };

  useEffect(() => {
    fetchBidTypes();
  }, [page, rowsPerPage, debouncedSearchTerm]);

  const validateField = (name: string, value: string): string => {
    switch (name) {
      case 'bidType':
        return !value.trim() ? 'Type cannot be blank' : '';
      default:
        return '';
    }
  };

  const handleBlur = (field: keyof TouchedFields) => {
    setTouched((prev) => ({
      ...prev,
      [field]: true
    }));

    const error = validateField(field, newBidType);
    setErrors((prev) => ({
      ...prev,
      [field]: error
    }));
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const clearForm = () => {
    setNewBidType('');
    setIsActive(true);
    setEditingId(null);
    setTouched({ bidType: false });
    setErrors({ bidType: '' });
    setMessage('');
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    const typeError = validateField('bidType', newBidType);
    setErrors({ bidType: typeError });
    setTouched({ bidType: true });

    if (typeError) {
      return;
    }

    try {
      setLoading(true);
      if (editingId) {
        // Update existing bid type
        const response = (await httpRequest(
          'PATCH',
          `/bid_portal/type/update/${editingId}`,
          {
            Type: newBidType,
            Active: isActive
          }
        )) as ApiResponse;

        if (response.status === 'success') {
          setMessage('Bid type updated successfully');
          toast.success('Bid type updated successfully');
          clearForm();
          fetchBidTypes();
        } else {
          setErrorMessage(response.message);
        }
      } else {
        // Create new bid type
        const response = (await httpRequest('POST', '/bid_portal/type/insert', {
          Type: newBidType
        })) as ApiResponse;

        if (response.status === 'success') {
          setMessage('Bid type created successfully');
          toast.success('Bid type created successfully');
          clearForm();
          fetchBidTypes();
        } else {
          setErrorMessage(response.message);
        }
      }
    } catch (error) {
      toast.error(
        editingId ? 'Failed to update bid type' : 'Failed to create bid type'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (bidType: BidType) => {
    setEditingId(bidType.TypeID);
    setNewBidType(bidType.Type);
    setIsActive(bidType.Active);
    setTouched({ bidType: false });
    setErrors({ bidType: '' });
    setMessage('');
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';

    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 3, md: 4 },
        bgcolor: alpha(theme.palette.background.default, 0.4),
        borderRadius: 2,
        minHeight: '100vh',
        width: '100%'
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 4 }}>
        <ClassIcon
          sx={{
            fontSize: 32,
            color: theme.palette.primary.main
          }}
        />
        <Typography
          variant="h4"
          sx={{
            color: theme.palette.primary.main,
            fontWeight: 'bold'
          }}
        >
          Bid Type Management
        </Typography>
      </Stack>

      {message && (
        <Fade in={!!message}>
          <Alert
            severity="success"
            sx={{
              mb: 3,
              boxShadow: 2,
              borderRadius: 2
            }}
            onClose={() => setMessage('')}
          >
            {message}
          </Alert>
        </Fade>
      )}

      {errorMessage && (
        <Fade in={!!errorMessage}>
          <Alert
            severity="error"
            sx={{
              mb: 3,
              boxShadow: 2,
              borderRadius: 2
            }}
            onClose={() => setErrorMessage('')}
          >
            {errorMessage}
          </Alert>
        </Fade>
      )}

      {userRole === 'Admin' && (
        <Card
          elevation={2}
          sx={{
            mb: 4,
            borderRadius: 2,
            overflow: 'visible'
          }}
        >
          <CardHeader
            title={
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {editingId ? (
                  <EditIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
                ) : (
                  <AddCircleOutlineIcon
                    sx={{ mr: 1, color: theme.palette.primary.main }}
                  />
                )}
                <Typography variant="h6">
                  {editingId ? 'Edit Bid Type' : 'Create New Bid Type'}
                </Typography>
                {editingId && (
                  <Chip
                    label={`ID: ${editingId}`}
                    size="small"
                    sx={{
                      ml: 2,
                      bgcolor: alpha(theme.palette.info.main, 0.1),
                      color: theme.palette.info.main
                    }}
                  />
                )}
              </Box>
            }
            sx={{
              bgcolor: alpha(theme.palette.primary.light, 0.1),
              pb: 1
            }}
          />
          <CardContent sx={{ p: 3 }}>
            <Box component="form" onSubmit={handleSubmit}>
              <Grid container spacing={3} alignItems="center">
                <Grid item xs={12} md={6}>
                  <TextField
                    required
                    label="Bid Type Name"
                    value={newBidType}
                    onChange={(e) => setNewBidType(e.target.value)}
                    onBlur={() => handleBlur('bidType')}
                    error={touched.bidType && !!errors.bidType}
                    helperText={touched.bidType && errors.bidType}
                    fullWidth
                    disabled={loading}
                    InputProps={{
                      sx: { borderRadius: 1 },
                      startAdornment: (
                        <InputAdornment position="start">
                          <ClassIcon
                            sx={{ color: theme.palette.primary.main }}
                          />
                        </InputAdornment>
                      )
                    }}
                  />
                </Grid>

                {editingId && (
                  <Grid item xs={12} md={4}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 1.5,
                        borderRadius: 1,
                        backgroundColor: alpha(
                          isActive
                            ? theme.palette.success.main
                            : theme.palette.error.main,
                          0.05
                        ),
                        border: `1px solid ${alpha(
                          isActive
                            ? theme.palette.success.main
                            : theme.palette.error.main,
                          0.2
                        )}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {isActive ? (
                          <CheckCircleOutlineIcon
                            sx={{ color: theme.palette.success.main, mr: 1 }}
                          />
                        ) : (
                          <HighlightOffIcon
                            sx={{ color: theme.palette.error.main, mr: 1 }}
                          />
                        )}
                        <Typography
                          variant="body1"
                          sx={{
                            color: isActive
                              ? theme.palette.success.main
                              : theme.palette.error.main,
                            fontWeight: 500
                          }}
                        >
                          {isActive ? 'Active' : 'Inactive'}
                        </Typography>
                      </Box>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={isActive}
                            onChange={(e) => setIsActive(e.target.checked)}
                            color={isActive ? 'success' : 'error'}
                            disabled={loading}
                          />
                        }
                        label=""
                        sx={{ m: 0 }}
                      />
                    </Paper>
                  </Grid>
                )}

                <Grid item xs={12} md={editingId ? 2 : 6}>
                  <Stack
                    direction="row"
                    spacing={2}
                    justifyContent={{ xs: 'flex-start', md: 'flex-end' }}
                  >
                    <Button
                      variant="outlined"
                      onClick={clearForm}
                      disabled={loading}
                      startIcon={<ClearIcon />}
                      sx={{
                        borderRadius: 1,
                        px: 2
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      disabled={loading}
                      startIcon={
                        loading ? (
                          <CircularProgress size={20} />
                        ) : editingId ? (
                          <SaveIcon />
                        ) : (
                          <AddCircleOutlineIcon />
                        )
                      }
                      sx={{
                        borderRadius: 1,
                        px: 3
                      }}
                    >
                      {loading ? 'Saving...' : editingId ? 'Update' : 'Create'}
                    </Button>
                  </Stack>
                </Grid>
              </Grid>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Table Section */}
      <Card elevation={2} sx={{ borderRadius: 2 }}>
        <CardHeader
          title={
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <ViewListIcon
                  sx={{ mr: 1, color: theme.palette.primary.main }}
                />
                <Typography variant="h6">All Bid Types</Typography>
                <Chip
                  label={`${totalRecords} total`}
                  size="small"
                  sx={{
                    ml: 2,
                    bgcolor: alpha(theme.palette.primary.light, 0.1),
                    color: theme.palette.primary.main
                  }}
                />
              </Box>

              <TextField
                placeholder="Search bid types..."
                size="small"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" color="primary" />
                    </InputAdornment>
                  ),
                  endAdornment: searchTerm && (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setSearchTerm('')}
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                  sx: {
                    borderRadius: 1,
                    bgcolor: alpha(theme.palette.background.default, 0.7)
                  }
                }}
                sx={{ width: { xs: '100%', sm: 250 } }}
              />
            </Box>
          }
          sx={{
            bgcolor: alpha(theme.palette.background.default, 0.5),
            borderBottom: `1px solid ${theme.palette.divider}`
          }}
        />

        <TableContainer sx={{ maxHeight: 650 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    fontWeight: 'bold',
                    bgcolor: theme.palette.background.paper,
                    py: 2
                  }}
                >
                  Bid Type
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 'bold',
                    bgcolor: theme.palette.background.paper
                  }}
                >
                  Status
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 'bold',
                    bgcolor: theme.palette.background.paper
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <CalendarTodayIcon sx={{ mr: 1, fontSize: 18 }} />
                    Created Date
                  </Box>
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 'bold',
                    bgcolor: theme.palette.background.paper
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <CalendarTodayIcon sx={{ mr: 1, fontSize: 18 }} />
                    Updated Date
                  </Box>
                </TableCell>
                {userRole === 'Admin' && (
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 'bold',
                      bgcolor: theme.palette.background.paper
                    }}
                  >
                    Actions
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {tableLoading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={30} />
                    <Typography
                      variant="body2"
                      sx={{ mt: 2, color: theme.palette.text.secondary }}
                    >
                      Loading bid types...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : bidTypes.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <Typography variant="body1" color="text.secondary">
                      {searchTerm
                        ? `No bid types matching "${searchTerm}" found`
                        : 'No bid types found'}
                    </Typography>
                    {searchTerm && (
                      <Button
                        variant="text"
                        color="primary"
                        startIcon={<ClearIcon />}
                        onClick={() => setSearchTerm('')}
                        sx={{ mt: 1 }}
                      >
                        Clear Search
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ) : (
                bidTypes.map((bidType) => (
                  <TableRow
                    key={bidType.TypeID}
                    sx={{
                      '&:nth-of-type(odd)': {
                        bgcolor: alpha(theme.palette.background.default, 0.3)
                      },
                      '&:hover': {
                        bgcolor: alpha(theme.palette.primary.light, 0.05)
                      }
                    }}
                  >
                    <TableCell sx={{ py: 2, fontWeight: 'medium' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <ClassIcon
                          sx={{
                            mr: 1,
                            color: theme.palette.primary.main,
                            fontSize: 18
                          }}
                        />
                        {bidType.Type}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={bidType.Active ? 'Active' : 'Inactive'}
                        icon={
                          bidType.Active ? (
                            <CheckCircleOutlineIcon />
                          ) : (
                            <HighlightOffIcon />
                          )
                        }
                        sx={{
                          backgroundColor: bidType.Active
                            ? alpha(theme.palette.success.main, 0.1)
                            : alpha(theme.palette.error.main, 0.1),
                          color: bidType.Active
                            ? theme.palette.success.main
                            : theme.palette.error.main,
                          fontWeight: 500,
                          borderRadius: 1,
                          '& .MuiChip-icon': {
                            color: 'inherit'
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Tooltip title={bidType.CreateDate} arrow>
                        <Typography variant="body2">
                          {formatDate(bidType.CreateDate)}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Tooltip title={bidType.UpdateDate || ''} arrow>
                        <Typography variant="body2">
                          {formatDate(bidType.UpdateDate)}
                        </Typography>
                      </Tooltip>
                    </TableCell>

                    {userRole === 'Admin' && (
                      <TableCell align="center">
                        <Tooltip title="Edit Bid Type" arrow>
                          <Button
                            onClick={() => handleEdit(bidType)}
                            size="small"
                            variant="text"
                            color="primary"
                            startIcon={<EditIcon />}
                            disabled={loading}
                            sx={{
                              borderRadius: 1,
                              '&:hover': {
                                backgroundColor: alpha(
                                  theme.palette.primary.main,
                                  0.04
                                )
                              }
                            }}
                          >
                            Edit
                          </Button>
                        </Tooltip>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Divider />
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={totalRecords}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            borderTop: `1px solid ${theme.palette.divider}`,
            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows':
              {
                my: 1
              }
          }}
        />
      </Card>

      {loading && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: alpha(theme.palette.background.default, 0.7),
            backdropFilter: 'blur(4px)',
            zIndex: 9999
          }}
        >
          <Paper
            elevation={4}
            sx={{
              p: 3,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              borderRadius: 2
            }}
          >
            <CircularProgress size={48} sx={{ mb: 2 }} />
            <Typography variant="subtitle1">
              {editingId ? 'Updating bid type...' : 'Creating bid type...'}
            </Typography>
          </Paper>
        </Box>
      )}
    </Box>
  );
};

export default BidTypeMaster;
